import {
  Code2,
  Wrench,
  Terminal,
  RotateCcw,
  AlertCircle,
  FolderOpen,
  Slash,
  Palette,
  LayoutPanelLeft,
  MapPin,
  FileCode2,
} from 'lucide-react';
import type { FeatureGroup } from '../types';

export const editorGroup: FeatureGroup = {
  id: 'editor',
  label: 'Editor & Code',
  headline: 'A first-class code editing experience',
  summary:
    'Meshpit uses the Monaco editor — the same engine as VS Code — running in a dedicated web worker so it never blocks the UI. A purpose-built SCAD toolbar, refactoring tools, slash commands, and inline error reporting keep you in flow.',
  accentClass: 'from-amber-500 to-yellow-400',
  items: [
    {
      icon: Code2,
      title: 'Monaco Editor',
      description:
        'Full-featured code editor with OpenSCAD syntax highlighting, autocomplete, bracket matching, and multi-cursor editing — running in a web worker for zero UI jank.',
      tier: 'major',
    },
    {
      icon: Wrench,
      title: 'SCAD Toolbar',
      description:
        'A toolbar with dialogs for inserting shapes, clones, matrix transforms, hull, minkowski, modifiers, text, and render settings — with one-click imports for the most used libraries.',
      tier: 'major',
    },
    {
      icon: Terminal,
      title: 'Refactoring Tools',
      description:
        'Extract selected geometry into a named module, extract all modules to separate files, rename symbols across the file, and reorganise code with automated refactoring actions.',
      tier: 'major',
    },
    {
      icon: AlertCircle,
      title: 'Inline Error Reporting',
      description:
        'Syntax errors and OpenSCAD compile errors appear as inline squiggles and gutter markers in the editor — with Markdown-rendered messages in the AI explain integration.',
      tier: 'major',
    },
    {
      icon: FolderOpen,
      title: 'Command Palette',
      description:
        'Open the command palette to quickly access any action — switch views, run exports, trigger AI features, or jump to settings — without reaching for the mouse.',
      tier: 'major',
    },
    {
      icon: Slash,
      title: 'Slash Mode & Autocomplete',
      description:
        'Type / in the command palette to enter slash mode. Ghost-text hints guide you through commands like /ai create <prompt> or /add cube — with inline autocomplete as you type.',
      tier: 'major',
    },
    {
      icon: Palette,
      title: 'Color Indicator Bar',
      description:
        'A color-coded bar alongside the code editor shows which lines correspond to which colored objects in the 3D scene — hover a color to highlight the matching geometry.',
      tier: 'major',
    },
    {
      icon: RotateCcw,
      title: 'Undo / Redo',
      description:
        'Full undo and redo history for all code edits, with keyboard shortcuts matching VS Code conventions.',
      tier: 'minor',
    },
    {
      icon: FileCode2,
      title: 'Parameter Insert Menu',
      description:
        'A dropdown menu in the code panel for inserting parameter type templates directly at the cursor — no need to memorise annotation syntax.',
      tier: 'minor',
    },
    {
      icon: LayoutPanelLeft,
      title: 'Flexible Panel Layout',
      description:
        'Each of the three panels — code, viewer, and parameters — can be independently collapsed. Three preset layouts (⌘1/2/3) let you switch between code-focused, balanced, and preview-focused views.',
      tier: 'minor',
    },
    {
      icon: MapPin,
      title: 'Cursor Position & Status',
      description:
        'The editor reports the current cursor line and column, and the toolbar reflects compile status, active color count, and other live state at a glance.',
      tier: 'minor',
    },
  ],
};
